/**
 * How far a pot of cash can grow if you keep buying and selling cars.
 * Deliberately cautious: only a few cars at once, and a bad car now and then.
 */

export interface CapitalPlanInput {
  startingCash: number;
  /** Cash tied up in one typical car. */
  cashPerCar: number;
  /** Profit on a typical car that goes to plan. */
  profitPerCar: number;
  /** Days from starting to look for a car to the cash arriving. */
  cycleDays: number;
  /** Most cars you can handle at once (space, time, insurance). */
  maxCars: number;
  /** One car in this many goes wrong (0 means none). */
  badCarEvery: number;
  /** How much of the cash in a bad car you lose, in percent. */
  badCarLossPct: number;
  /** Add profit back into the pot (true) or bank it (false). */
  reinvest: boolean;
  months: number;
}

export interface CapitalRound {
  round: number;
  startDay: number;
  carsBought: number;
  cashIn: number;
  profit: number;
  potAfter: number;
}

export interface CapitalPlan {
  rounds: CapitalRound[];
  finalPot: number;
  totalProfit: number;
  carsSold: number;
  /** Average profit per car once bad cars are allowed for. */
  expectedProfitPerCar: number;
}

/** Average profit per car with one bad car in every `badCarEvery` allowed for. */
export function expectedProfitPerCar(i: Pick<CapitalPlanInput, 'profitPerCar' | 'cashPerCar' | 'badCarEvery' | 'badCarLossPct'>): number {
  if (i.badCarEvery <= 0) return i.profitPerCar;
  const share = 1 / i.badCarEvery;
  const loss = (i.cashPerCar * i.badCarLossPct) / 100;
  return i.profitPerCar * (1 - share) - loss * share;
}

export function planCapital(i: CapitalPlanInput): CapitalPlan {
  const perCar = expectedProfitPerCar(i);
  const horizon = Math.max(0, Math.round(i.months * 30.4));
  const cycle = Math.max(1, Math.round(i.cycleDays));
  const maxCars = Math.max(0, Math.floor(i.maxCars));
  let pot = Math.max(0, i.startingCash);
  let banked = 0;
  const rounds: CapitalRound[] = [];
  if (i.cashPerCar <= 0 || maxCars === 0) {
    return { rounds, finalPot: pot, totalProfit: 0, carsSold: 0, expectedProfitPerCar: perCar };
  }
  let day = 0;
  let round = 0;
  while (day + cycle <= horizon) {
    const affordable = Math.floor(pot / i.cashPerCar);
    const cars = Math.min(maxCars, affordable);
    if (cars <= 0) break;
    round += 1;
    const cashIn = cars * i.cashPerCar;
    const profit = cars * perCar;
    // The cash comes back after the sale; profit either joins the pot or is banked.
    if (i.reinvest) pot += profit;
    else banked += profit;
    day += cycle;
    rounds.push({ round, startDay: day - cycle, carsBought: cars, cashIn, profit, potAfter: pot + banked });
  }
  const totalProfit = rounds.reduce((s, r) => s + r.profit, 0);
  return {
    rounds,
    finalPot: pot + banked,
    totalProfit,
    carsSold: rounds.reduce((s, r) => s + r.carsBought, 0),
    expectedProfitPerCar: perCar,
  };
}
